<script>
/* eslint-env browser */
/* global FB */
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

import ChannelApi from '../../../../../api/channels';
import PageHeader from '../../SettingsSubPageHeader.vue';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import { buildFacebookLoginScopes } from 'dashboard/helper/facebookScopes';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    PageHeader,
    NextButton,
    Dialog,
  },
  setup() {
    const { accountId } = useAccount();
    const { replaceInstallationName } = useBranding();
    return {
      accountId,
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      channel: 'facebook',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
      isErrorDialogOpen: false,
      // Bumps on each login/reset so a late fetchPages from a previous attempt
      // cannot overwrite a cancel (FB_AUTH) with FB_PAGES_ALL_CONNECTED.
      loginAttemptId: 0,
      errorModal: {
        code: '',
        httpStatus: null,
        details: '',
      },
    };
  },

  validations: {
    pageName: {
      required,
    },

    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
    facebookLoginScopes() {
      // Include Instagram scopes on create so linked IG inboxes can send/receive DMs
      // without waiting for a later reauthorization_required prompt.
      return buildFacebookLoginScopes({ includeInstagramScopes: true });
    },
    errorModalDescription() {
      const descriptions = {
        FB_SDK_LOAD: this.$t(
          'INBOX_MGMT.ADD.FB.ERROR_MODAL.DESCRIPTIONS.FB_SDK_LOAD'
        ),
        FB_AUTH: this.$t('INBOX_MGMT.ADD.FB.ERROR_MODAL.DESCRIPTIONS.FB_AUTH'),
        FB_UNAUTHORIZED: this.$t(
          'INBOX_MGMT.ADD.FB.ERROR_MODAL.DESCRIPTIONS.FB_UNAUTHORIZED'
        ),
        FB_PAGES_FETCH: this.$t(
          'INBOX_MGMT.ADD.FB.ERROR_MODAL.DESCRIPTIONS.FB_PAGES_FETCH'
        ),
        FB_PAGES_EMPTY: this.$t(
          'INBOX_MGMT.ADD.FB.ERROR_MODAL.DESCRIPTIONS.FB_PAGES_EMPTY'
        ),
        FB_PAGES_ALL_CONNECTED: this.$t(
          'INBOX_MGMT.ADD.FB.ERROR_MODAL.DESCRIPTIONS.FB_PAGES_ALL_CONNECTED'
        ),
      };

      return (
        descriptions[this.errorModal.code] ||
        this.$t('INBOX_MGMT.ADD.FB.ERROR_MODAL.DESCRIPTIONS.FB_AUTH')
      );
    },
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
    // Warm the SDK so the login click opens its popup within the gesture window.
    this.loadFBsdk().catch(() => {});
  },

  methods: {
    async startLogin() {
      this.loginAttemptId += 1;
      const attemptId = this.loginAttemptId;
      this.hasLoginStarted = true;
      try {
        // this will load the SDK in a promise, and resolve it when the sdk is loaded
        // in case the SDK is already present, it will resolve immediately
        await this.loadFBsdk();
        if (attemptId !== this.loginAttemptId) return;
        this.runFBInit(); // run init anyway, `tryFBlogin` won't wait for `fbAsyncInit` otherwise.
        this.tryFBlogin(attemptId); // make an attempt to login
      } catch (error) {
        if (attemptId !== this.loginAttemptId) return;
        if (error.name === 'ScriptLoaderError') {
          this.showErrorModal({ code: 'FB_SDK_LOAD' });
        } else {
          Sentry.captureException(error);
          this.showErrorModal({ code: 'FB_AUTH', details: error.message });
        }
      }
    },

    setPageName({ name }) {
      this.v$.selectedPage.$touch();
      this.pageName = name;
    },

    initChannelAuth(channel) {
      if (channel === 'facebook') {
        this.loadFBsdk();
      }
    },

    runFBInit() {
      FB.init({
        appId: window.chatwootConfig.fbAppId,
        xfbml: true,
        version: window.chatwootConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin(attemptId) {
      FB.login(
        response => {
          if (attemptId !== this.loginAttemptId) return;

          this.hasError = false;
          const accessToken = response?.authResponse?.accessToken;
          // Closing the Meta popup yields no token (status unknown) → FB_AUTH.
          // Only fetch pages when Meta actually returned a token.
          if (accessToken) {
            this.fetchPages(accessToken, attemptId);
            return;
          }
          // eslint-disable-next-line no-console
          console.error('FACEBOOK AUTH ERROR', response);
          if (response?.status === 'not_authorized') {
            this.showErrorModal({ code: 'FB_UNAUTHORIZED' });
          } else {
            this.showErrorModal({ code: 'FB_AUTH' });
          }
        },
        {
          scope: this.facebookLoginScopes,
        }
      );
    },

    async fetchPages(_token, attemptId = this.loginAttemptId) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        if (attemptId !== this.loginAttemptId) return;

        const {
          data: { data },
        } = response;
        this.pageList = data.page_details || [];
        this.user_access_token = data.user_access_token;

        const selectablePages = this.pageList.filter(item => !item.exists);
        if (!selectablePages.length) {
          const code = this.pageList.length
            ? 'FB_PAGES_ALL_CONNECTED'
            : 'FB_PAGES_EMPTY';
          this.showErrorModal({ code });
        }
      } catch (error) {
        if (attemptId !== this.loginAttemptId) return;
        Sentry.captureException(error);
        this.showErrorModal({
          code: error.response?.data?.code || 'FB_PAGES_FETCH',
          httpStatus: error.response?.status || null,
          details: error.response?.data?.error || error.message || '',
        });
      }
    },

    showErrorModal({ code, httpStatus = null, details = '' }) {
      this.loginAttemptId += 1;
      this.errorModal = { code, httpStatus, details };
      this.user_access_token = '';
      this.pageList = [];
      this.hasError = false;
      this.hasLoginStarted = false;
      this.isErrorDialogOpen = true;
      this.$nextTick(() => {
        this.$refs.errorDialog?.open();
      });
    },

    handleErrorRetry() {
      this.$refs.errorDialog?.close();
    },

    handleErrorClose() {
      // Dialog emits close on outside clicks even when closed; only reset after a real error modal.
      if (!this.isErrorDialogOpen) return;
      this.isErrorDialogOpen = false;
      this.resetLoginState();
    },

    resetLoginState() {
      this.loginAttemptId += 1;
      this.hasLoginStarted = false;
      this.hasError = false;
      this.isCreating = false;
      this.user_access_token = '';
      this.pageList = [];
      this.selectedPage = { name: null, id: null };
      this.pageName = '';
      this.errorModal = { code: '', httpStatus: null, details: '' };
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name,
      };
    },

    createChannel() {
      this.v$.$touch();
      if (!this.v$.$error) {
        this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
        this.isCreating = true;
        this.$store
          .dispatch('inboxes/createFBChannel', this.channelParams())
          .then(data => {
            this.$router.replace({
              name: 'settings_inboxes_add_agents',
              params: { page: 'new', inbox_id: data.id },
            });
          })
          .catch(error => {
            const errorMessage = parseAPIErrorResponse(error);
            useAlert(
              typeof errorMessage === 'string'
                ? errorMessage
                : this.$t('INBOX_MGMT.DETAILS.ERROR_CHANNEL_CREATION')
            );
            this.isCreating = false;
          });
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <div
      v-if="!hasLoginStarted"
      class="flex flex-col items-center justify-center h-full text-center"
    >
      <a href="#" @click="startLogin()">
        <img
          class="w-auto h-10 rounded-md"
          src="~dashboard/assets/images/channels/facebook_login.png"
          alt="Facebook-logo"
        />
      </a>
      <p class="py-6">
        {{ replaceInstallationName($t('INBOX_MGMT.ADD.FB.HELP')) }}
      </p>
    </div>
    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
      <form
        v-else
        class="flex flex-col flex-wrap mx-0"
        @submit.prevent="createChannel()"
      >
        <div class="w-full">
          <PageHeader
            :header-title="$t('INBOX_MGMT.ADD.DETAILS.TITLE')"
            :header-content="
              replaceInstallationName($t('INBOX_MGMT.ADD.DETAILS.DESC'))
            "
          />
        </div>
        <div class="w-3/5">
          <div class="w-full">
            <div class="input-wrap" :class="{ error: v$.selectedPage.$error }">
              {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE') }}
              <multiselect
                v-model="selectedPage"
                close-on-select
                allow-empty
                :options="getSelectablePages"
                track-by="id"
                label="name"
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
                selected-label
                @select="setPageName"
              />
              <span v-if="v$.selectedPage.$error" class="message">
                {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
              </span>
            </div>
          </div>
          <div class="w-full">
            <label :class="{ error: v$.pageName.$error }">
              {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
              <input
                v-model="pageName"
                type="text"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
                @input="v$.pageName.$touch"
              />
              <span v-if="v$.pageName.$error" class="message">
                {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
              </span>
            </label>
          </div>
          <div class="w-full text-right">
            <NextButton :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')" />
          </div>
        </div>
      </form>
    </div>

    <Dialog
      ref="errorDialog"
      type="alert"
      width="md"
      :title="$t('INBOX_MGMT.ADD.FB.ERROR_MODAL.TITLE')"
      :description="errorModalDescription"
      :confirm-button-label="$t('INBOX_MGMT.ADD.FB.ERROR_MODAL.RETRY')"
      :show-cancel-button="false"
      @confirm="handleErrorRetry"
      @close="handleErrorClose"
    >
      <div
        class="rounded-md border border-n-slate-6 bg-n-slate-2 p-3 text-left text-sm text-n-slate-12"
      >
        <p class="mb-1 font-medium">
          {{ $t('INBOX_MGMT.ADD.FB.ERROR_MODAL.SUPPORT_HINT') }}
        </p>
        <p class="mb-0 font-mono text-xs">
          {{ $t('INBOX_MGMT.ADD.FB.ERROR_MODAL.CODE_LABEL') }}:
          {{ errorModal.code }}
        </p>
        <p v-if="errorModal.httpStatus" class="mb-0 font-mono text-xs">
          HTTP: {{ errorModal.httpStatus }}
        </p>
        <p
          v-if="errorModal.details"
          class="mb-0 mt-1 break-all font-mono text-xs text-n-slate-11"
        >
          {{ errorModal.details }}
        </p>
      </div>
    </Dialog>
  </div>
</template>
